import { TRACKING_CONFIG } from '@intent/shared/tracking';
import type { EventInput, EventType } from '@intent/shared';
import type { Transport } from './transport';

export interface QueueSession {
  visitorId: string;
  sessionId: string;
}

/**
 * Buffers events and ships them in batches.
 *
 * Every visitor action would otherwise be one request; batching keeps the
 * host page's network quiet. A flush is triggered by whichever comes first:
 * the timer, a full buffer, or the page going away.
 */
export class EventQueue {
  private buffer: EventInput[] = [];
  private timer: number | null = null;
  private flushing = false;
  private session: QueueSession | null = null;

  constructor(
    private readonly transport: Transport,
    /** Notified when the server rotates the session out from under us. */
    private readonly onSessionRotated: (sessionId: string) => void,
  ) {}

  setSession(session: QueueSession): void {
    this.session = session;
  }

  enqueue(type: EventType, payload: EventInput['payload'] = {}): void {
    this.buffer.push({
      type,
      url: window.location.href,
      referrer: document.referrer || null,
      occurredAt: new Date().toISOString(),
      payload,
    });

    if (this.buffer.length >= TRACKING_CONFIG.batchMaxSize) {
      void this.flush();
      return;
    }

    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.timer !== null) return;

    this.timer = window.setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, TRACKING_CONFIG.batchFlushIntervalMs);
  }

  /**
   * Sends everything buffered.
   *
   * On failure the batch is put back at the head of the queue so ordering is
   * preserved and nothing is silently dropped — the next flush retries it.
   */
  async flush(): Promise<void> {
    if (this.flushing || this.buffer.length === 0 || !this.session) return;

    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    const batch = this.buffer.splice(0, TRACKING_CONFIG.batchMaxSize);
    this.flushing = true;

    try {
      const result = await this.transport.sendEvents({
        visitorId: this.session.visitorId,
        sessionId: this.session.sessionId,
        events: batch,
      });

      if (result.sessionId !== this.session.sessionId) {
        this.session = { ...this.session, sessionId: result.sessionId };
        this.onSessionRotated(result.sessionId);
      }
    } catch {
      this.buffer.unshift(...batch);
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Last-chance delivery during page unload. Uses `sendBeacon`, which the
   * browser completes even after the document is gone; a normal fetch would
   * be cancelled.
   */
  flushOnUnload(): void {
    if (this.buffer.length === 0 || !this.session) return;

    const batch = this.buffer.splice(0, TRACKING_CONFIG.batchMaxSize);
    const delivered = this.transport.sendEventsBeacon({
      visitorId: this.session.visitorId,
      sessionId: this.session.sessionId,
      events: batch,
    });

    if (!delivered) {
      this.buffer.unshift(...batch);
    }
  }

  get size(): number {
    return this.buffer.length;
  }
}
